import { create } from "zustand";
import { DownloadedFileWithIds } from "@/pages/workspace/providers/SharePointProvider";
import {
  SerializedFileMetadataMap,
  SerializedFolderMetadataMap,
} from "@/types/folder-metadata";

export type ConflictingFile = {
  file: File;
  folderPath: string; // The folder path where the conflict occurs (e.g., "folder1/subfolder" or "" for root)
};

interface UploadState {
  // Conflict handling
  conflictingFiles: File[] | ConflictingFile[];
  conflictingFilesWithPath: ConflictingFile[]; // New field to store files with path information
  isConflictDialogOpen: boolean;

  // Pending uploads from computer
  pendingUploadFiles: FileList | null;
  pendingWorkspaceFileNames: Record<string, string>;
  pendingFolderId: string | undefined;

  // Pending uploads from OneDrive
  pendingOneDriveFiles: DownloadedFileWithIds[];

  // Upload metadata dialog
  isUploadMetadataDialogOpen: boolean;
  pendingMetadataFiles: FileList | null;
  pendingMetadataWorkspaceFileNames: Record<string, string>;
  pendingMetadataFolderId: string | undefined;
  pendingFolderMetadataMap: SerializedFolderMetadataMap;
  pendingFileMetadataMap: SerializedFileMetadataMap;

  // Upload progress
  isUploadInProgress: boolean;

  // Actions
  setConflictingFiles: (files: File[] | ConflictingFile[]) => void;
  setConflictingFilesWithPath: (files: ConflictingFile[]) => void;
  setIsConflictDialogOpen: (open: boolean) => void;

  setPendingUploadFiles: (files: FileList | null) => void;
  setPendingWorkspaceFileNames: (names: Record<string, string>) => void;
  setPendingFolderId: (id: string | undefined) => void;

  setPendingOneDriveFiles: (files: DownloadedFileWithIds[]) => void;

  setIsUploadMetadataDialogOpen: (open: boolean) => void;
  setPendingMetadataFiles: (files: FileList | null) => void;
  setPendingMetadataWorkspaceFileNames: (names: Record<string, string>) => void;
  setPendingMetadataFolderId: (id: string | undefined) => void;
  setPendingFolderMetadataMap: (map: SerializedFolderMetadataMap) => void;
  setPendingFileMetadataMap: (map: SerializedFileMetadataMap) => void;

  setIsUploadInProgress: (inProgress: boolean) => void;

  // Helper methods
  clearConflictState: () => void;
  clearPendingUploadState: () => void;
  clearPendingOneDriveState: () => void;
  clearPendingMetadataState: () => void;
  clearAll: () => void;
}

export const useUploadStateStore = create<UploadState>((set) => ({
  // Initial state
  conflictingFiles: [],
  conflictingFilesWithPath: [],
  isConflictDialogOpen: false,
  pendingUploadFiles: null,
  pendingWorkspaceFileNames: {},
  pendingFolderId: undefined,
  pendingOneDriveFiles: [],
  isUploadMetadataDialogOpen: false,
  pendingMetadataFiles: null,
  pendingMetadataWorkspaceFileNames: {},
  pendingMetadataFolderId: undefined,
  pendingFolderMetadataMap: null,
  pendingFileMetadataMap: null,
  isUploadInProgress: false,

  // Setters
  setConflictingFiles: (files) => set({ conflictingFiles: files }),
  setConflictingFilesWithPath: (files) =>
    set({ conflictingFilesWithPath: files }),
  setIsConflictDialogOpen: (open) => set({ isConflictDialogOpen: open }),

  setPendingUploadFiles: (files) => set({ pendingUploadFiles: files }),
  setPendingWorkspaceFileNames: (names) =>
    set({ pendingWorkspaceFileNames: names }),
  setPendingFolderId: (id) => set({ pendingFolderId: id }),

  setPendingOneDriveFiles: (files) => set({ pendingOneDriveFiles: files }),

  setIsUploadMetadataDialogOpen: (open) =>
    set({ isUploadMetadataDialogOpen: open }),
  setPendingMetadataFiles: (files) => set({ pendingMetadataFiles: files }),
  setPendingMetadataWorkspaceFileNames: (names) =>
    set({ pendingMetadataWorkspaceFileNames: names }),
  setPendingMetadataFolderId: (id) => set({ pendingMetadataFolderId: id }),
  setPendingFolderMetadataMap: (map) => set({ pendingFolderMetadataMap: map }),
  setPendingFileMetadataMap: (map) => set({ pendingFileMetadataMap: map }),

  setIsUploadInProgress: (inProgress) =>
    set({ isUploadInProgress: inProgress }),

  // Helper methods
  clearConflictState: () =>
    set({
      conflictingFiles: [],
      conflictingFilesWithPath: [],
      isConflictDialogOpen: false,
    }),

  clearPendingUploadState: () =>
    set({
      pendingUploadFiles: null,
      pendingWorkspaceFileNames: {},
      pendingFolderId: undefined,
    }),

  clearPendingOneDriveState: () =>
    set({
      pendingOneDriveFiles: [],
    }),

  clearPendingMetadataState: () =>
    set({
      isUploadMetadataDialogOpen: false,
      pendingMetadataFiles: null,
      pendingMetadataWorkspaceFileNames: {},
      pendingMetadataFolderId: undefined,
      pendingFolderMetadataMap: null,
      pendingFileMetadataMap: null,
    }),

  clearAll: () =>
    set({
      conflictingFiles: [],
      conflictingFilesWithPath: [],
      isConflictDialogOpen: false,
      pendingUploadFiles: null,
      pendingWorkspaceFileNames: {},
      pendingFolderId: undefined,
      pendingOneDriveFiles: [],
      isUploadMetadataDialogOpen: false,
      pendingMetadataFiles: null,
      pendingMetadataWorkspaceFileNames: {},
      pendingMetadataFolderId: undefined,
      pendingFolderMetadataMap: null,
      pendingFileMetadataMap: null,
      isUploadInProgress: false,
    }),
}));
